import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
} from "../components/ui/drawer";
import { Input } from "../components/ui/input";
import { api, formatApiErrorDetail } from "../lib/api";
import { exportQuotationPdf } from "../lib/exportQuotationPdf";
import { exportQuotationExcel } from "../lib/exportQuotationExcel";
import { useCart } from "../context/CartContext";
import {
  EnvelopeSimple, ShareNetwork, FilePdf, FileXls, TrashSimple, PaperPlaneRight,
} from "@phosphor-icons/react";

export function QuoteActionsSheet({ open, onOpenChange, cart, items }) {
  const { deleteCart } = useCart();
  const navigate = useNavigate();
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  if (!cart) return null;

  const quotedItems = items.map((it) => ({ ...it, price: it.final_price }));

  const close = () => { setEmailMode(false); setEmail(""); onOpenChange(false); };

  const handleEmailSend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/carts/${cart.id}/email-quote`, { recipient_email: email, base_url: window.location.origin });
      toast.success(`Quote sent to ${email}`);
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/carts/${cart.id}/quote-link`, null, { params: { base_url: window.location.origin } });
      if (navigator.share) {
        await navigator.share({ title: `Quote: ${cart.name}`, url: data.quote_url });
      } else {
        await navigator.clipboard.writeText(data.quote_url);
        toast.success("Link copied to clipboard");
      }
      close();
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate link");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleExportPdf = async () => {
    setBusy(true);
    try {
      await exportQuotationPdf(quotedItems, cart.name, cart.currency);
      close();
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setBusy(true);
    try {
      await exportQuotationExcel(quotedItems, cart.name, cart.currency);
      close();
    } catch {
      toast.error("Failed to export Excel");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteCart(cart.id);
      toast.success("Quotation deleted");
      close();
      navigate("/m/quotes", { replace: true });
    } catch {
      toast.error("Failed to delete");
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={close}>
      <DrawerContent className="mobile-app rounded-t-[28px] border-[#E7E5E4]" data-testid="mobile-quote-actions-sheet">
        <DrawerHeader>
          <DrawerTitle className="font-display text-2xl text-[#1C1917]">{emailMode ? "Email Quote" : "Quote Actions"}</DrawerTitle>
        </DrawerHeader>

        {emailMode ? (
          <form onSubmit={handleEmailSend} className="px-5 pb-8 space-y-4">
            <p className="text-sm text-[#78716C]">Send "{cart.name}" to a customer, including a self-serve online calculator link.</p>
            <Input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              data-testid="mobile-email-quote-input"
              className="h-13 rounded-[14px] border-[#E7E5E4] text-base"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="mobile-email-quote-send"
              className="press-scale w-full h-14 rounded-[16px] bg-[#1E3A8A] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors duration-200"
            >
              <PaperPlaneRight size={18} weight="bold" /> {busy ? "Sending..." : "Send Quote"}
            </button>
          </form>
        ) : (
          <div className="px-5 pb-8 space-y-2.5">
            <button onClick={() => setEmailMode(true)} data-testid="mobile-quote-action-email" className="press-scale w-full flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-[16px] p-4 transition-colors duration-200">
              <EnvelopeSimple size={20} className="text-[#1E3A8A]" />
              <span className="text-base font-medium text-[#1C1917]">Email Quote</span>
            </button>
            <button onClick={handleShare} disabled={busy} data-testid="mobile-quote-action-share" className="press-scale w-full flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-[16px] p-4 transition-colors duration-200 disabled:opacity-60">
              <ShareNetwork size={20} className="text-[#1E3A8A]" />
              <span className="text-base font-medium text-[#1C1917]">Share Link</span>
            </button>
            <button onClick={handleExportPdf} disabled={busy} data-testid="mobile-quote-action-pdf" className="press-scale w-full flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-[16px] p-4 transition-colors duration-200 disabled:opacity-60">
              <FilePdf size={20} className="text-[#1E3A8A]" />
              <span className="text-base font-medium text-[#1C1917]">Export PDF</span>
            </button>
            <button onClick={handleExportExcel} disabled={busy} data-testid="mobile-quote-action-excel" className="press-scale w-full flex items-center gap-3 bg-white border border-[#E7E5E4] rounded-[16px] p-4 transition-colors duration-200 disabled:opacity-60">
              <FileXls size={20} className="text-[#1E3A8A]" />
              <span className="text-base font-medium text-[#1C1917]">Export Excel</span>
            </button>
            <button onClick={handleDelete} disabled={busy} data-testid="mobile-quote-action-delete" className="press-scale w-full flex items-center gap-3 bg-[#9A3412]/8 border border-[#9A3412]/20 rounded-[16px] p-4 transition-colors duration-200 disabled:opacity-60 mt-4">
              <TrashSimple size={20} className="text-[#9A3412]" />
              <span className="text-base font-medium text-[#9A3412]">Delete Quotation</span>
            </button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
